import Divider from '@/components/common/Divider';
import ExportCSV from '@/components/common/ExportCSV';
import Tooltip from '@/components/common/Tooltip';
import { LuceraBlotterItem } from '@/model/blotters';
import { createLumeFxRestClient } from '@/services/LumeFxService';
import { useAppDispatch } from '@/state/hooks';
import { setHistoricTrades } from '@/state/reducers/blotterSlice';
import useLanguage from '@/utils/hooks/useLanguage';
import { copyStyles } from '@/utils/popout';
import { useLucera } from '@/utils/providers/LuceraProvider';
import { useScreenSizes } from '@/utils/providers/ScreenSizesProvider';
import cn from 'classnames';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { isTablet } from 'react-device-detect';
import { createPortal } from 'react-dom';
import { MdOpenInNew } from 'react-icons/md';
import useSWR from 'swr';
import Loader from '../common/Loader';
import PopOut, { PopOutHeader, PopOutTitle } from '../common/PopOut';
import LuceraOrderHistoryTable from '../modules/Blotters/LuceraOrderHistoryTable';
import Modal, { ModalClose, ModalHeader, ModalProps, ModalTitle } from './Modal';

interface LuceraDealHistoryModalProps extends ModalProps {
    order: LuceraBlotterItem;
    linkedOrder?: LuceraBlotterItem;
}

const LuceraDealHistoryModal = (props: LuceraDealHistoryModalProps) => {
    const { opened, handlers } = props;
    const dispatch = useAppDispatch();

    const lang = useLanguage();
    const screen = useScreenSizes();
    const [CSVData, setCSVData] = useState<any[]>([]);
    const [popOut, setPopOut] = useState<Window | null>(null);

    useEffect(() => {
        dispatch(setHistoricTrades(null));
        if (opened && popOut) {
            popOut.close();
            setPopOut(null);
        }
    }, [opened]);

    const openPopOutWindow = () => {
        const popOutWindow = window.open('', '', 'width=1200,height=600');
        if (popOutWindow) {
            copyStyles(document, popOutWindow.document);
            setPopOut(popOutWindow);
            handlers.close();
        }
    };

    return (
        <Fragment>
            <Modal {...props} size="sm:max-w-[90vw] h-full lg:h-[600px]">
                <ModalHeader>
                    <div className="flex space-x-2 items-center">
                        <ModalTitle>{lang.orderForm.orderHistory}</ModalTitle>
                        <ExportCSV className="w-5 h-5" csvData={CSVData} />
                        {(screen.laptop || screen.desktop) && !isTablet && (
                            <Tooltip
                                width={120}
                                content={lang.orderForm.openInSeparateWindow}
                                containerClassName="h-5"
                                placement="top-start">
                                <button type="button" className="text-xl" onClick={openPopOutWindow}>
                                    <MdOpenInNew />
                                </button>
                            </Tooltip>
                        )}
                    </div>
                    <ModalClose handlers={handlers} />
                </ModalHeader>
                <DealHistoryBody setCSVData={setCSVData} {...props} />
            </Modal>
            {popOut &&
                createPortal(
                    <PopOut>
                        <PopOutHeader>
                            <PopOutTitle>{lang.orderForm.orderHistory}</PopOutTitle>
                            <Tooltip content={lang.orderForm.downloadToCSV} className="-bottom-10 w-max">
                                <ExportCSV className="w-5 h-5" csvData={CSVData} />
                            </Tooltip>
                        </PopOutHeader>
                        <DealHistoryBody setCSVData={setCSVData} {...props} />
                    </PopOut>,
                    popOut.document.body
                )}
        </Fragment>
    );
};

export default LuceraDealHistoryModal;

const luceraRestURL = window.config?.lucera?.rest;

interface DealHistoryBodyProps extends LuceraDealHistoryModalProps {
    setCSVData: (data: any[]) => void;
}

const DealHistoryBody = (props: DealHistoryBodyProps) => {
    const { order, linkedOrder, setCSVData } = props;

    const lang = useLanguage();
    const { jwt } = useLucera();

    const lumeFxRestClient = createLumeFxRestClient({
        baseUrl: luceraRestURL,
        authorization: jwt
    });

    const { data, isLoading } = useSWR(
        'orderHistory' + order.clientOrderId,
        () => fetchOrderHistory(lumeFxRestClient, order),
        { refreshInterval: 5000 }
    );

    const { data: linkedData, isLoading: isLoadingLinkedData } = useSWR(
        'orderHistory' + linkedOrder?.clientOrderId,
        () => fetchOrderHistory(lumeFxRestClient, linkedOrder),
        { refreshInterval: 5000 }
    );

    const [orderHistory, setOrderHistory] = useState('Current');

    const orderHistoryData = useMemo(() => {
        if (data?.Data) return data.Data;
        return [];
    }, [data]);

    const linkedOrderHistoryData = useMemo(() => {
        if (linkedData?.Data) return linkedData.Data;
        return [];
    }, [linkedData]);

    const parentStrategyID = useMemo(() => {
        if (linkedOrder) return linkedOrder.clOrdLinkId === order.clOrdLinkId ? linkedOrder.clOrdLinkId : null;
        return null;
    }, [linkedOrder]);

    useEffect(() => {
        console.log({ data, linkedData, orderHistory, parentStrategyID });
    }, [data, linkedData, orderHistory, parentStrategyID]);

    return (
        <div className="flex flex-col w-full h-full">
            <Divider />
            {linkedOrder && (
                <div className="flex p-2">
                    <button
                        className={cn('text-sm py-1 px-2 text-brand-text border', {
                            'border-brand-primary bg-brand-primary': orderHistory === 'Current',
                            'border-brand-neutral-600 hover:bg-brand-primary-light hover:border-brand-primary-light':
                                orderHistory !== 'Current',
                            'border-r-0': true
                        })}
                        onClick={() => setOrderHistory('Current')}>
                        {lang.orderForm.currentOrder}
                    </button>
                    <button
                        className={cn('text-sm py-1 px-2 text-brand-text border', {
                            'border-brand-primary bg-brand-primary': orderHistory === 'Linked',
                            'border-brand-neutral-600 hover:bg-brand-primary-light hover:border-brand-primary-light':
                                orderHistory !== 'Linked'
                        })}
                        onClick={() => setOrderHistory('Linked')}>
                        {lang.orderForm.linkedOrder}
                    </button>
                </div>
            )}
            {parentStrategyID && <div className="text-brand-text text-sm px-2">{parentStrategyID}</div>}

            {(isLoading || isLoadingLinkedData) && (
                <div className="flex justify-center items-center flex-1">
                    <Loader className="text-brand-primary" />
                </div>
            )}

            {!(isLoading || isLoadingLinkedData) && orderHistoryData.length && (
                <div className="flex-1 basis-0 p-2 overflow-hidden">
                    <LuceraOrderHistoryTable
                        orderHistories={orderHistory === 'Current' ? orderHistoryData : linkedOrderHistoryData}
                        setCSVData={setCSVData}
                    />
                </div>
            )}

            {!(isLoading || isLoadingLinkedData) && !orderHistoryData?.length && (
                <div className="historic-search-empty p-2 flex justify-center items-center text-center flex-1 text-brand-neutral-400 text-sm font-bold italic">
                    <div>{lang.orderForm.noHistoryWasFoundForThisOrder}</div>
                </div>
            )}
        </div>
    );
};

async function fetchOrderHistory(lumeFxRestClient, order) {
    if (order) return (await lumeFxRestClient.getOrderStatus({ clordid: order.clientOrderId })) || [];
    return [];
}
